import { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { User } from '@/types/User';
import axios from 'axios';
import { getApiEndpoint, getImageURL } from '@/utils/ERPUtils';
import { Role } from '@/components/roles-modules/types';

import { logger } from '@/utils/logger';
interface UserFilters {
  search: string;
  role: string;
  status: string;
}

interface UserStats {
  total: number;
  active: number;
  pending: number;
  inactive: number;
}

export const useUserManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    role: 'all',
    status: 'all'
  });
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  // Memoized statistics calculation
  const stats = useMemo<UserStats>(() => {
    const total = users.length;
    const active = users.filter(u => u.status === 'active').length;
    const pending = users.filter(u => u.is_pending).length;
    const inactive = users.filter(u => u.status === 'inactive').length;

    return { total, active, pending, inactive };
  }, [users]);

  // Filtering now happens server-side (see fetchUsers below). The hook
  // exposes `filteredUsers` for backwards compatibility but it's identical
  // to `users` - the API returns only matching rows.
  const filteredUsers = users;

  // Admin users count (memoized)
  const adminCount = useMemo(() => {
    return users.filter(u =>
      u.roles.includes('Admin') || u.roles.includes('super_admin')
    ).length;
  }, [users]);

  const fetchRoles = useCallback(async () => {
    try {
      const rolesResponse = await axios.get(getApiEndpoint("GET_ALL_ROLES"))
      const rolesData = rolesResponse.data.message.data

      if(rolesResponse?.data?.message?.stats == "error") throw Error(rolesResponse.data.message.message)
      setRoles(rolesData || []);
    } catch (error) {
      logger.error('Error fetching roles:', error);
    }
  }, []);

  const fetchUsers = useCallback(async (overrides?: Partial<UserFilters>) => {
    try {
      setLoading(true);

      // Translate UI filter values into backend params. The backend
      // expects empty strings (or absent keys) for "no filter".
      const effective = { ...filters, ...(overrides || {}) };
      const params: Record<string, string | number> = { size: 1000 };
      if (effective.search) params.search = effective.search;
      if (effective.role && effective.role !== 'all') params.role = effective.role;
      if (effective.status && effective.status !== 'all') params.status = effective.status;

      // Fetch users with their profiles and roles
      const profiles = await axios.get(getApiEndpoint("GET_ALL_USERS"), { params, timeout: 30000 })

      // handle error case

      if(profiles.data.message.status === "error") throw Error(profiles.data.message.message)

      // Create users array with optimized mapping
      const usersData = profiles?.data?.message?.data?.map(profile => {

        
        let fullName = profile.full_name || profile.first_name || profile.email;

        // Get user roles - handle both string and already-parsed array
        const roles:Role[] = typeof profile?.roles === 'string'
          ? JSON.parse(profile?.roles || '[]')
          : (profile?.roles || []);

        return {
          id: profile.email,
          name: profile.full_name || profile.email?.split('@')[0] || 'Unknown',
          email: profile.email || '',
          status: profile.enabled ? 'active' : 'inactive',
          full_name:fullName,
          department: profile.department || "",
          first_name:profile?.first_name || "",
          last_name:profile?.last_name || "",
          username: profile?.username|| "",
          middle_name:profile?.middle_name || "",
          gender: profile?.gender || "",
          birthdate: profile?.birth_date || "",
          interests:profile?.interest|| "",
          roles: roles?.map(e=>e?.name) || [],
          lastActive: profile.last_login,
          avatar: getImageURL(profile?.user_image) || "",
          employee_id: profile.email,
          phone: profile?.phone || "",
          mobile_number: profile?.mobile_no || "",
          location: profile?.location || "",
          job_role: profile?.job_role || "",
          bio: profile?.bio || "",
          timezone: profile?.time_zone || "",
          created_on: profile?.creation || "",
          notes: "",
          email_confirmed: true,
          last_login: profile.last_login,
          last_active: profile.last_active,
          is_pending:profile?.pending_user == 1 ? true : false,
        } as User;
      }) || [];

      setUsers(usersData);
    } catch (error) {
      logger.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const updateFilters = useCallback((newFilters: Partial<UserFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const toggleUserSelection = useCallback((userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  }, []);

  const selectAllUsers = useCallback(() => {
    setSelectedUsers(filteredUsers.map(u => u.id));
  }, [filteredUsers]);

  const clearSelection = useCallback(() => {
    setSelectedUsers([]);
  }, []);

  const isAllSelected = useMemo(() =>
    filteredUsers.length > 0 && selectedUsers.length === filteredUsers.length,
    [selectedUsers.length, filteredUsers.length]
  );

  // Initial roles fetch (one-shot).
  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // Refetch users whenever filters change. Debounce the search field so
  // typing doesn't fire a request per keystroke; role/status changes still
  // refetch immediately because they're discrete values.
  useEffect(() => {
    const debounceMs = filters.search ? 300 : 0;
    const handle = setTimeout(() => {
      fetchUsers();
    }, debounceMs);
    return () => clearTimeout(handle);
  }, [filters.search, filters.role, filters.status, fetchUsers]);

  return {
    // Data
    users,
    roles,
    filteredUsers,
    stats: { ...stats, adminCount },

    // Loading state
    loading,

    // Filters
    filters,
    updateFilters,

    // Selection
    selectedUsers,
    toggleUserSelection,
    selectAllUsers,
    clearSelection,
    isAllSelected,

    // Actions
    fetchUsers,
    fetchRoles
  };
};